import type {
  CustomEnrichment,
  CustomEnrichmentVerdict,
  Lead,
} from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

/**
 * How a lead answers the campaign's custom question. Each answer carries the
 * agent's `verdict`; answers stored before it existed fall back to their
 * opening "Oui -" / "Non -" (or "Yes" / "No"), as the backend's public case
 * study does (`lead_serializer._public_custom_statement`).
 */
export type CustomQueryVerdict = CustomEnrichmentVerdict

// The verdict word and the separator after it ("Oui - ", "No: ").
const AFFIRMATIVE_RE = /^(oui|yes)\b\s*[-:–—]*\s*/i
const NEGATIVE_PREFIX_RE = /^(non|no)\b\s*[-:–—]*\s*/i
// A negative answer that states itself without the verdict word.
const NEGATIVE_STATEMENT_RE = /^(aucun|aucune|pas)\b/i

/** The verdict of a single stored answer: the agent's, else its wording's. */
export function entryVerdict(
  entry: Pick<CustomEnrichment, "value" | "verdict">,
): CustomQueryVerdict {
  if (entry.verdict) return entry.verdict
  const text = entry.value.trim()
  if (AFFIRMATIVE_RE.test(text)) return "yes"
  if (NEGATIVE_PREFIX_RE.test(text) || NEGATIVE_STATEMENT_RE.test(text)) {
    return "no"
  }
  return "unknown"
}

/** An answer without its verdict word, first letter capitalized. */
export function customQueryStatement(value: string): string {
  const text = value
    .trim()
    .replace(AFFIRMATIVE_RE, "")
    .replace(NEGATIVE_PREFIX_RE, "")
    .trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function answers(lead: Pick<Lead, "custom_enrichment">): CustomEnrichment[] {
  return (lead.custom_enrichment ?? []).filter((entry) => entry.value.trim())
}

/**
 * The lead-level verdict: "yes" as soon as one answer says yes, "no" when
 * every answer says no, "unknown" otherwise, null when the question was never
 * answered. Person-level answers (`people[].custom_enrichment`) are not read.
 */
export function customQueryVerdict(
  lead: Pick<Lead, "custom_enrichment">,
): CustomQueryVerdict | null {
  const verdicts = answers(lead).map(entryVerdict)
  if (verdicts.length === 0) return null
  if (verdicts.includes("yes")) return "yes"
  if (verdicts.every((verdict) => verdict === "no")) return "no"
  return "unknown"
}
